import { CliBaseCommand } from '../BaseCommand';
import { AwsProvider, FsProvider, GcpProvider } from '../providers';

import { AnchorProvider, Program, Wallet, web3 } from '@coral-xyz/anchor';
import { Flags } from '@oclif/core';
import {
  ON_DEMAND_DEVNET_PID,
  ON_DEMAND_MAINNET_PID,
} from '@switchboard-xyz/on-demand';

export type SolanaCluster = web3.Cluster | 'localnet';

export abstract class SolanaBaseCommand extends CliBaseCommand {
  static flags = {
    ...CliBaseCommand.flags,
    mainnetBeta: Flags.boolean({
      description: 'WARNING: use mainnet-beta solana cluster',
      required: false,
      exclusive: ['cluster'],
      default: false,
    }),
    cluster: Flags.string({
      description: 'the solana cluster to connect to',
      options: ['devnet', 'mainnet-beta', 'mainnet', 'localnet'],
      required: false,
      exclusive: ['mainnetBeta'],
      aliases: ['network'],
    }),
    rpcUrl: Flags.string({
      char: 'u',
      description: 'alternate RPC url',
    }),
    programId: Flags.string({
      description: 'alternative Switchboard program ID to interact with',
    }),
    commitment: Flags.string({
      description: 'commitment level to use with our connection',
      default: 'confirmed',
      options: ['confirmed', 'finalized', 'processed'],
    }),
  };

  private _cluster: SolanaCluster;

  private _rpcUrl: string;

  private _programId?: web3.PublicKey;

  private _connection: web3.Connection;

  private _priorityFee: number;

  private _computeUnitLimitMultiple: number;

  get cluster() {
    return this._cluster;
  }

  get connection() {
    return this._connection;
  }

  get programId() {
    return (this._programId ?? this._cluster === 'mainnet-beta')
      ? ON_DEMAND_MAINNET_PID
      : ON_DEMAND_DEVNET_PID;
  }

  get provider() {
    return new AnchorProvider(this.connection, new Wallet(new web3.Keypair()), {
      commitment: this.connection.commitment,
    });
  }

  get program(): Promise<Program> {
    return Program.at(this.programId, this.provider);
  }

  async init() {
    await super.init();
    const { flags } = await this.parse(
      this.constructor as typeof SolanaBaseCommand
    );

    this._cluster = this.computeCluster(flags.cluster, flags.mainnetBeta);
    this._rpcUrl = this.computeRpcUrl(this._cluster, flags.rpcUrl);

    const commitment: web3.Commitment = (() => {
      switch (flags.commitment) {
        case 'confirmed':
        case 'finalized':
        case 'processed':
          return flags.commitment;
        default:
          return 'confirmed';
      }
    })();
    this._connection = new web3.Connection(this._rpcUrl, { commitment });

    // TODO: Load connection params from config
    this.logConfig({ cluster: this._cluster, rpc: this._rpcUrl });
  }

  toTxUrl(signature: string) {
    return `https://explorer.solana.com/tx/${signature}?cluster=${this._cluster}`;
  }

  toAccountUrl(account: web3.PublicKeyInitData) {
    const publicKey = new web3.PublicKey(account);
    return `https://explorer.solana.com/address/${publicKey}?cluster=${this._cluster}`;
  }

  private computeCluster(
    clusterOption?: string,
    mainnetFlag?: boolean
  ): SolanaCluster {
    if (mainnetFlag) {
      return 'mainnet-beta';
    }

    if (clusterOption) {
      switch (clusterOption) {
        case 'mainnet':
        case 'mainnet-beta': {
          return 'mainnet-beta';
        }

        case 'devnet': {
          return 'devnet';
        }

        case 'localnet': {
          return 'localnet';
        }
      }
    }

    return 'devnet';
  }

  private computeRpcUrl(cluster: SolanaCluster, rpcUrlFlag?: string): string {
    // If user has specifically set an rpc url, use it.
    if (rpcUrlFlag) return rpcUrlFlag;

    // If user is connecting to localnet, use the local rpc url.
    if (cluster === 'localnet') return 'http://127.0.0.1:8899';

    const rpcUrl = this.ctx.getRpcUrl(
      'solana',
      cluster === 'mainnet-beta' ? 'mainnet' : cluster
    );
    if (!rpcUrl) {
      throw new Error(
        `Failed to get Solana RPC URL for cluster ${cluster}. Try providing the --rpcUrl flag`
      );
    }

    return rpcUrl;
  }

  async getSigner(keypairPath: string): Promise<web3.Keypair> {
    const parseKeypairString = (fileString: string): web3.Keypair => {
      // check if bytes
      const parsedFileString = fileString
        .trim()
        .replace(/\n/g, '')
        .replace(/\s/g, '');
      const bytesRegex = /^\[(\s)?\d+((\s)?,(\s)?\d+){31,}]/;
      if (bytesRegex.test(parsedFileString)) {
        return web3.Keypair.fromSecretKey(
          new Uint8Array(JSON.parse(parsedFileString))
        );
      }

      try {
        return web3.Keypair.fromSecretKey(
          new Uint8Array(JSON.parse(parsedFileString))
        );
      } catch {} // eslint-disable-line no-empty

      throw new Error('Failed to derive secret key from input file');
    };

    const errors: unknown[] = [];

    // try loading keypair from filesystem
    try {
      const normalizedKeypairPath = this.normalizePath(keypairPath);
      const keypair = FsProvider.getSecret(
        normalizedKeypairPath,
        parseKeypairString
      );
      return keypair;
    } catch (error) {
      errors.push(error);
    }

    // try loading keypair from gcp secret manager
    try {
      const keypair = await GcpProvider.getSecret(
        keypairPath,
        parseKeypairString
      );
      return keypair;
    } catch (error) {
      errors.push(error);
    }

    // try loading keypair from aws secrets
    try {
      const keypair = await AwsProvider.getSecret(
        keypairPath,
        parseKeypairString
      );
      return keypair;
    } catch (error) {
      errors.push(error);
    }

    throw new Error(
      `Failed to load Solana keypair ${keypairPath}\n${errors
        .map(error => {
          if (error instanceof Error) return error.message;
          return String(error);
        })
        .join('\n')}`
    );
  }

  normalizeAccountData(
    publicKey: web3.PublicKey,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: Record<string, any>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Record<string, any> {
    const object = {
      publicKey: publicKey.toString(),
      ...data,
    };
    return JSON.parse(JSON.stringify(object, this.jsonReplacers, 2));
  }
}
